import React from 'react';
import { StageSection, Prose, KeyPoints, FactGrid } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';

export const meta = {
    title: 'Keys & Addresses',
    tagline: 'Wallets store permission to spend, not coins themselves.',
};

// This stage expects 3 quiz questions configured in the admin panel, in this order:
//   Q0 - what a wallet actually stores (keys, not coins)
//   Q1 - what happens if a private key is permanently lost
//   Q2 - why addresses include a checksum
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 2 of 8" title="A private key, a public key, and an address">
            <Prose>
                Every Bitcoin address traces back to a private key: a random 256-bit number. Elliptic-curve
                cryptography (the secp256k1 curve, the same curve family Ethereum uses) derives a public key from
                that private key, and the address is a shorter, checksummed encoding derived from the public key by
                hashing it.
            </Prose>
            <FactGrid items={[
                ['Private key', 'A secret random number. Whoever holds it can authorize spending. Never shared.'],
                ['Public key', 'Derived mathematically from the private key. Safe to reveal; can\'t be reversed back into the private key.'],
                ['Address', 'A shorter, checksummed encoding derived from the public key. What you give someone to receive funds.'],
            ]} columns={3} />
        </StageSection>
    )),

    content('wallets-store-keys', () => (
        <StageSection title="A wallet doesn't hold coins">
            <Prose>
                There's no file anywhere labeled "your bitcoin." The network's ledger records which outputs exist
                and what conditions unlock them (more on this in the next stage). "Owning bitcoin" really means
                holding a private key capable of producing a valid signature that satisfies one of those conditions.
                A wallet app's job is to manage those keys and build correctly signed transactions on your behalf.
            </Prose>
        </StageSection>
    )),

    question(0),

    content('signatures', () => (
        <StageSection title="Signatures prove authorization without exposing the key">
            <Prose>
                To spend funds, you produce a digital signature over the transaction using your private key. Anyone
                can verify that signature against your public key without ever seeing the private key itself. This
                is what lets a stranger on the network confirm you're authorized to spend, without you handing over
                anything secret.
            </Prose>
            <KeyPoints items={[
                <>Whoever can produce a valid signature for an address controls its funds. There is no password reset and no customer support line.</>,
                <>Losing a private key with no backup permanently locks whatever funds it controlled; nobody can restore access.</>,
            ]} />
        </StageSection>
    )),

    question(1),

    content('address-formats', () => (
        <StageSection title="Addresses encode more than just the destination">
            <Prose>
                Bitcoin addresses come in a few different formats (older and newer encodings exist side by side),
                but every format includes a built-in checksum. If you mistype even a single character of an address,
                the checksum almost certainly won't match, and the wallet will reject it rather than silently
                sending funds to an unintended (but validly formatted) address.
            </Prose>
        </StageSection>
    )),

    question(2),
];
